import type { Ref } from 'vue'
import { inject } from 'vue'
import type { AxiosInstance } from 'axios'
import { fetchAll } from '@lib/util/network'
import type { PitcherInfo } from '@lib/types/app'
import type { Tag, Instance } from '@openapi'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { InstanceTagsCanvasType, InstanceTagsType } from '@lib/types/instanceTags.types'

export default function useInstanceTagsActions() {
  const pitcherInfo = inject<Ref<PitcherInfo>>('pitcherInfo')
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')

  async function fetchAllInstanceTagsRequest(params: { instanceId?: Instance['id'] }): Promise<PaginatedData<Tag>> {
    if (!restApiAxios?.value) throw new Error('HTTP client is not initialized')

    return await restApiAxios.value.get<unknown, PaginatedData<Tag>>('tags/', { params })
  }

  async function fetchAllInstanceTags({ search, instanceId }: { search?: string; instanceId?: Instance['id'] }) {
    const reqParams = {
      search,
      instance_id: instanceId ?? pitcherInfo?.value?.instanceId,
    }

    return await fetchAll<Tag>(fetchAllInstanceTagsRequest, reqParams)
  }

  async function fetchInstanceTags({
    search,
    instanceId,
    page = 1,
    pageSize = 50,
    includeHidden,
    canvasType,
    type,
  }: {
    search: string
    instanceId?: Instance['id']
    page?: number
    pageSize?: number
    includeHidden?: boolean
    canvasType?: InstanceTagsCanvasType
    type?: InstanceTagsType[]
  }) {
    const reqParams = {
      page,
      page_size: pageSize,
      search,
      instance_id: instanceId ?? pitcherInfo?.value?.instanceId,
      include_hidden: includeHidden,
      canvas_type: canvasType,
      type,
    }

    if (search.length < 3) return []

    if (!restApiAxios?.value) throw new Error('HTTP client is not initialized')

    const response = await restApiAxios.value.get<unknown, PaginatedData<Tag>>('tags/', { params: reqParams })

    return response.results
  }

  return {
    fetchInstanceTags,
    fetchAllInstanceTags,
  }
}
